/* BOJOURNAL — product.js
   Viewing-room behaviour, loaded by the product sections only.
   <media-gallery>   thumbnails, swipe counter, keyboard, zoom dialog
   <product-info>    sticky only when the panel fits the viewport
   <variant-picker>  variant changes through the Section Rendering API
   <product-recommendations>  deferred related works
   Spec disclosures: columns on desktop, accordions on phones. */
(() => {
  const B = window.Bojournal;
  if (!B || !B.Component || customElements.get('media-gallery')) return;

  const pad = (n) => String(n).padStart(2, '0');

  /* ------------------------------------------------------------------
     Media gallery
     ------------------------------------------------------------------ */
  class MediaGallery extends B.Component {
    init() {
      this.list = this.querySelector('[data-media-list]');
      this.items = [...this.querySelectorAll('[data-media-index]')];
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.current = this.querySelector('[data-media-current]');
      this.section = this.closest('[data-product-section]');
      this.dialog = this.section && this.section.querySelector('[data-lightbox]');
      this.zoomables = [...this.querySelectorAll('[data-zoom]')];
      this.index = 0;
      if (!this.list) return;

      // Phones: horizontal swipe — the counter follows the scroll position.
      this.on(
        this.list,
        'scroll',
        () => {
          cancelAnimationFrame(this._raf);
          this._raf = requestAnimationFrame(() => {
            if (this.list.scrollWidth <= this.list.clientWidth + 2) return;
            const width = this.list.clientWidth || 1;
            this.setCurrent(Math.round(Math.abs(this.list.scrollLeft) / width));
          });
        },
        { passive: true }
      );

      // Desktop: vertical stack — the thumbnail follows what is in view.
      if ('IntersectionObserver' in window && this.thumbs.length) {
        this._io = new IntersectionObserver(
          (entries) => {
            if (this.list.scrollWidth > this.list.clientWidth + 2) return;
            const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
            if (visible) this.setCurrent(Number(visible.target.dataset.mediaIndex));
          },
          { threshold: [0.35, 0.6] }
        );
        this.items.forEach((item) => this._io.observe(item));
      }

      this.thumbs.forEach((thumb, i) => this.on(thumb, 'click', () => this.goTo(i, true)));

      // Arrow keys move between views when focus is inside the gallery.
      this.on(this, 'keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
        if (event.target.closest('video, iframe, input')) return;
        const rtl = getComputedStyle(this).direction === 'rtl';
        const forward = event.key === 'ArrowDown' || event.key === (rtl ? 'ArrowLeft' : 'ArrowRight');
        const next = Math.min(Math.max(this.index + (forward ? 1 : -1), 0), this.items.length - 1);
        if (next === this.index) return;
        event.preventDefault();
        this.goTo(next, true);
      });

      this.zoomables.forEach((button) => this.on(button, 'click', () => this.openZoom(this.zoomables.indexOf(button))));
      this.bindDialog();
      this.bindLens();
    }

    /* ---------- Texture lens ----------
       Mouse and trackpad: a round lens follows the pointer over a painting
       and shows it about 2.6× closer, from the full-size photograph, so the
       brushwork and the surface can be read. Touch screens keep pinch and
       the zoom dialog. */
    bindLens() {
      const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
      if (!fine.matches || !this.classList.contains('pdp__media--artwork')) return;
      const FACTOR = 2.6;
      const SIZE = 200;
      this.zoomables.forEach((button) => {
        const img = button.querySelector('img');
        if (!img) return;
        let lens = null;
        let hiRes = null;
        let raf = 0;
        let point = null;
        const content = () => {
          const r = img.getBoundingClientRect();
          const nw = img.naturalWidth || r.width;
          const nh = img.naturalHeight || r.height;
          const scale = Math.min(r.width / nw, r.height / nh);
          const w = nw * scale;
          const h = nh * scale;
          return { left: r.left + (r.width - w) / 2, top: r.top + (r.height - h) / 2, width: w, height: h };
        };
        const paint = () => {
          raf = 0;
          if (!lens || !point) return;
          const box = content();
          const x = point.x - box.left;
          const y = point.y - box.top;
          const inside = x >= 0 && y >= 0 && x <= box.width && y <= box.height;
          lens.classList.toggle('is-on', inside);
          button.classList.toggle('is-lensing', inside);
          if (!inside) return;
          const b = button.getBoundingClientRect();
          lens.style.transform = 'translate3d(' + (point.x - b.left - SIZE / 2).toFixed(1) + 'px,' + (point.y - b.top - SIZE / 2).toFixed(1) + 'px,0)';
          lens.style.backgroundSize = (box.width * FACTOR).toFixed(1) + 'px ' + (box.height * FACTOR).toFixed(1) + 'px';
          lens.style.backgroundPosition = (SIZE / 2 - x * FACTOR).toFixed(1) + 'px ' + (SIZE / 2 - y * FACTOR).toFixed(1) + 'px';
        };
        this.on(button, 'pointerenter', () => {
          if (!lens) {
            lens = document.createElement('span');
            lens.className = 'pdp__lens';
            lens.setAttribute('aria-hidden', 'true');
            lens.style.width = SIZE + 'px';
            lens.style.height = SIZE + 'px';
            lens.style.backgroundImage = 'url("' + (img.currentSrc || img.src) + '")';
            button.appendChild(lens);
          }
          if (!hiRes && button.dataset.zoom) {
            hiRes = new Image();
            hiRes.onload = () => {
              if (lens) lens.style.backgroundImage = 'url("' + hiRes.src + '")';
            };
            hiRes.src = button.dataset.zoom;
          }
        });
        this.on(button, 'pointermove', (event) => {
          point = { x: event.clientX, y: event.clientY };
          if (!raf) raf = requestAnimationFrame(paint);
        });
        this.on(button, 'pointerleave', () => {
          point = null;
          if (lens) lens.classList.remove('is-on');
          button.classList.remove('is-lensing');
        });
      });
    }

    teardown() {
      if (this._io) this._io.disconnect();
    }

    setCurrent(i) {
      if (Number.isNaN(i) || i === this.index) return;
      this.index = i;
      if (this.current) this.current.textContent = pad(i + 1);
      this.thumbs.forEach((thumb, n) => {
        thumb.classList.toggle('is-current', n === i);
        if (n === i) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    }

    goTo(i, focus) {
      const item = this.items[i];
      if (!item) return;
      const behavior = B.reduceMotion.matches ? 'auto' : 'smooth';
      if (this.list.scrollWidth > this.list.clientWidth + 2) {
        item.scrollIntoView({ block: 'nearest', inline: 'start', behavior });
      } else {
        const header = document.querySelector('site-header');
        const offset = (header ? header.offsetHeight : 0) + 16;
        window.scrollTo({ top: Math.max(0, item.getBoundingClientRect().top + window.scrollY - offset), behavior });
      }
      this.setCurrent(i);
      if (focus) {
        const target = item.querySelector('button, video, iframe, a');
        if (target) target.focus({ preventScroll: true });
      }
    }

    /* ---------- Zoom dialog ---------- */
    bindDialog() {
      if (!this.dialog || this.dialog._bound) return;
      this.dialog._bound = true;
      const d = this.dialog;
      d.querySelector('[data-lightbox-close]').addEventListener('click', () => d.close());
      d.querySelector('[data-lightbox-prev]').addEventListener('click', () => this.step(-1));
      d.querySelector('[data-lightbox-next]').addEventListener('click', () => this.step(1));
      d.addEventListener('click', (event) => {
        if (event.target === d) d.close();
      });
      d.addEventListener('keydown', (event) => {
        const rtl = getComputedStyle(d).direction === 'rtl';
        if (event.key === (rtl ? 'ArrowLeft' : 'ArrowRight')) {
          event.preventDefault();
          this.step(1);
        } else if (event.key === (rtl ? 'ArrowRight' : 'ArrowLeft')) {
          event.preventDefault();
          this.step(-1);
        }
      });
      // Swipe between images on touch screens.
      let x0 = null;
      d.addEventListener('touchstart', (e) => (x0 = e.touches.length === 1 ? e.touches[0].clientX : null), { passive: true });
      d.addEventListener('touchend', (e) => {
        if (x0 === null || d.querySelector('.is-actual')) return;
        const dx = e.changedTouches[0].clientX - x0;
        const rtl = getComputedStyle(d).direction === 'rtl';
        if (Math.abs(dx) > 48) this.step((dx < 0) !== rtl ? 1 : -1);
        x0 = null;
      });
      d.addEventListener('close', () => {
        B.scrollLock.off();
        const back = this.zoomables[this.zoomIndex];
        if (back && document.contains(back)) back.focus({ preventScroll: true });
        this.goTo(this.items.indexOf(back && back.closest('[data-media-index]')));
      });
    }

    openZoom(i) {
      const d = this.dialog;
      const button = this.zoomables[i];
      if (!button) return;
      if (!d || typeof d.showModal !== 'function') {
        window.open(button.dataset.zoom, '_blank', 'noopener');
        return;
      }
      this.render(i);
      B.scrollLock.on();
      d.showModal();
      d.querySelector('[data-lightbox-close]').focus();
    }

    step(delta) {
      const n = this.zoomables.length;
      if (n < 2) return;
      this.render((this.zoomIndex + delta + n) % n);
    }

    render(i) {
      const d = this.dialog;
      const button = this.zoomables[i];
      this.zoomIndex = i;
      const stage = d.querySelector('[data-lightbox-stage]');
      const img = new Image();
      img.decoding = 'async';
      img.src = button.dataset.zoom;
      img.alt = button.dataset.zoomAlt || '';
      img.width = Number(button.dataset.zoomW) || 2000;
      img.height = Number(button.dataset.zoomH) || 2000;
      img.className = 'lightbox__img';
      img.addEventListener('click', () => stage.classList.toggle('is-actual'));
      stage.classList.remove('is-actual');
      stage.replaceChildren(img);
      const many = this.zoomables.length > 1;
      d.querySelector('[data-lightbox-counter]').textContent = many ? `${pad(i + 1)} / ${pad(this.zoomables.length)}` : '';
      d.querySelectorAll('.lightbox__nav').forEach((nav) => (nav.hidden = !many));
    }
  }

  B.define('media-gallery', MediaGallery);

  /* ------------------------------------------------------------------
     Product info: sticky only when the whole panel fits in the viewport,
     so a long panel is never cut off with no way to reach its end.
     ------------------------------------------------------------------ */
  class ProductInfo extends B.Component {
    init() {
      this.panel = this.querySelector('[data-panel]');
      const desktop = window.matchMedia('(min-width: 990px)');
      const check = () => {
        /* Measure against the condensed header height the panel sticks under. */
        const probe = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-sticky')) || 4.25;
        const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        const room = window.innerHeight - probe * rem - 48;
        this.classList.toggle('is-sticky', desktop.matches && this.panel && this.panel.offsetHeight <= room);
      };
      if ('ResizeObserver' in window && this.panel) {
        this._ro = new ResizeObserver(check);
        this._ro.observe(this.panel);
      }
      this.on(window, 'resize', check, { passive: true });
      check();
    }

    teardown() {
      if (this._ro) this._ro.disconnect();
    }
  }

  B.define('product-info', ProductInfo);

  /* ------------------------------------------------------------------
     Buy bar (phones): visible only while the main action is scrolled
     out of view above, and never over the footer. Mirrors the main
     button's label and state; clicking it presses the real button.
     ------------------------------------------------------------------ */
  class BuyBar extends B.Component {
    init() {
      this.section = this.closest('[data-product-section]');
      const main = () => this.section && this.section.querySelector('[data-add-button], [data-enquire-primary]');
      const mobile = window.matchMedia('(max-width: 989px)');
      let mainAbove = false;
      let footerVisible = false;
      const update = () => {
        const show = mobile.matches && mainAbove && !footerVisible && !!main();
        if (show === !this.hidden) return;
        if (show) {
          this.hidden = false;
          requestAnimationFrame(() => this.classList.add('is-visible'));
        } else {
          this.classList.remove('is-visible');
          window.setTimeout(() => {
            if (!this.classList.contains('is-visible')) this.hidden = true;
          }, B.duration('--dur-3'));
        }
        document.body.classList.toggle('has-buy-bar', show);
      };
      // Position check on scroll (rAF-throttled) rather than an
      // IntersectionObserver: a jump from below the button to above it
      // (anchor link, End key) crosses no threshold and would be missed.
      let ticking = false;
      const measure = () => {
        ticking = false;
        const target = main();
        const footer = document.querySelector('footer');
        mainAbove = !!target && target.getBoundingClientRect().bottom < 0;
        footerVisible = !!footer && footer.getBoundingClientRect().top < window.innerHeight;
        update();
      };
      const request = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(measure);
      };
      this.on(window, 'scroll', request, { passive: true });
      this.on(window, 'resize', request, { passive: true });
      this.on(mobile, 'change', request);
      measure();

      const bar = this.querySelector('[data-buy-bar-add]');
      const label = this.querySelector('[data-buy-bar-label]');
      this.on(bar, 'click', () => {
        const button = main();
        if (button && !button.disabled) button.click();
      });
      // Mirror label and busy/done state from the real button; re-watch when
      // a variant change re-renders the purchase block.
      this._mo = new MutationObserver(() => {
        const button = main();
        if (!button) return;
        if (label) {
          const source = button.querySelector('[data-add-label]');
          if (source) label.textContent = source.textContent.trim();
        }
        if (bar) {
          bar.disabled = button.disabled;
          bar.classList.toggle('is-loading', button.classList.contains('is-loading'));
          bar.classList.toggle('is-done', button.classList.contains('is-done'));
        }
      });
      const buy = this.section && this.section.querySelector('[data-product-buy]');
      if (buy) this._mo.observe(buy, { subtree: true, childList: true, characterData: true, attributes: true });
    }

    teardown() {
      if (this._mo) this._mo.disconnect();
      document.body.classList.remove('has-buy-bar');
    }
  }

  B.define('buy-bar', BuyBar);

  /* ------------------------------------------------------------------
     Share: the device's own share sheet where it exists, otherwise the
     link is copied and a short status is announced.
     ------------------------------------------------------------------ */
  class ShareButton extends B.Component {
    init() {
      const status = this.querySelector('.share__status');
      this.on(this.querySelector('button'), 'click', async () => {
        const data = { title: this.dataset.title, url: this.dataset.url };
        try {
          if (navigator.share) {
            await navigator.share(data);
            return;
          }
          await navigator.clipboard.writeText(data.url);
          if (status) {
            status.textContent = this.dataset.copied;
            window.setTimeout(() => (status.textContent = ''), 2400);
          }
        } catch (error) {
          /* the visitor dismissed the share sheet: nothing to do */
        }
      });
    }
  }

  B.define('share-button', ShareButton);

  /* ------------------------------------------------------------------
     Variant picker
     ------------------------------------------------------------------ */
  class VariantPicker extends B.Component {
    init() {
      const data = this.querySelector('[data-variants]');
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.on(this, 'change', () => this.onChange());
    }

    selectedOptions() {
      return [...this.querySelectorAll('fieldset')].map((set) => {
        const checked = set.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    onChange() {
      const options = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((value, i) => value === options[i]));
      const section = this.closest('[data-product-section]');
      const input = section.querySelector('[data-variant-input]');
      const button = section.querySelector('[data-add-button]');
      const label = button && button.querySelector('[data-add-label]');

      if (!variant) {
        if (button) button.disabled = true;
        if (label) label.textContent = (B.strings && B.strings.unavailable) || 'Unavailable';
        return;
      }
      if (input) input.value = variant.id;
      const url = `${this.dataset.productUrl}?variant=${variant.id}`;
      window.history.replaceState({}, '', url);
      section.setAttribute('aria-busy', 'true');

      const controller = (this._fetch = new AbortController());
      fetch(`${url}&section_id=${this.dataset.section}`, { signal: controller.signal })
        .then((response) => response.text())
        .then((html) => {
          if (controller !== this._fetch) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          ['[data-product-price]', '[data-product-buy]'].forEach((selector) => {
            const fresh = doc.querySelector(selector);
            const current = section.querySelector(selector);
            if (fresh && current) current.innerHTML = fresh.innerHTML;
          });
          section.removeAttribute('aria-busy');
          if (variant.featured_media) {
            const gallery = section.querySelector('media-gallery');
            const item = section.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
            if (gallery && item) gallery.goTo(Number(item.dataset.mediaIndex));
          }
        })
        .catch((error) => {
          if (error.name !== 'AbortError') window.location.href = url;
        });
    }
  }

  B.define('variant-picker', VariantPicker);

  /* ------------------------------------------------------------------
     Recommendations (fallback for related works)
     ------------------------------------------------------------------ */
  class ProductRecommendations extends B.Component {
    init() {
      if (this.loaded) return;
      const load = () => {
        this.loaded = true;
        fetch(this.dataset.url)
          .then((response) => response.text())
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const fresh = doc.querySelector('.related');
            const host = this.closest('.related');
            if (fresh && fresh.querySelector('.track') && host) {
              host.innerHTML = fresh.innerHTML;
              B.reveal(host);
            } else {
              const empty = this.closest('[data-related-host]');
              (empty || this).remove();
            }
          })
          .catch(() => this.remove());
      };
      if (!('IntersectionObserver' in window)) return load();
      this._io = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          this._io.disconnect();
          load();
        },
        { rootMargin: '600px 0px' }
      );
      this._io.observe(this);
    }

    teardown() {
      if (this._io) this._io.disconnect();
    }
  }

  B.define('product-recommendations', ProductRecommendations);

  /* ------------------------------------------------------------------
     Specification disclosures
     Desktop: every group open, reading as columns. Phones: accordions,
     first group open. Re-applied after Theme Editor reloads.
     ------------------------------------------------------------------ */
  /* Quiet in-page links ("Specifications", "Shipping…") only stay when the
     section they point to actually rendered — no dead anchors. */
  const pruneJumps = () => {
    document.querySelectorAll('.pdp__jumps').forEach((list) => {
      list.querySelectorAll('a[href^="#"]').forEach((a) => {
        const id = decodeURIComponent(a.getAttribute('href').slice(1));
        if (!id || !document.getElementById(id)) a.closest('li').remove();
      });
      if (!list.querySelector('li')) list.remove();
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', pruneJumps);
  else pruneJumps();
  document.addEventListener('shopify:section:load', pruneJumps);

  const desktop = window.matchMedia('(min-width: 990px)');
  const applySpecs = (root = document) => {
    root.querySelectorAll('[data-spec-group]').forEach((group, i) => {
      group.open = desktop.matches ? true : i === 0;
    });
  };
  document.addEventListener('click', (event) => {
    const summary = event.target.closest('[data-spec-group] > summary');
    if (summary && desktop.matches) event.preventDefault();
  });
  desktop.addEventListener('change', () => applySpecs());
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => applySpecs());
  else applySpecs();
  document.addEventListener('shopify:section:load', (event) => applySpecs(event.target));
})();
